<template>
  <div>
    <CCard>
      <CCardHeader>
        <h2>
          <span>ユーザ管理</span>
          <span class="card-header-actions">
            <router-link :to="{ name: registerTop }"><button class="btn btn-primary">新規登録</button></router-link>
          </span>
        </h2>
      </CCardHeader>
      <CCardBody>
        <CAlert v-if="errors.length" color="danger" close-button @update:show="errors = []">
          <div v-for="error in errors" :key="error">{{ error }}</div>
        </CAlert>

        <ListSearchBox
          :value="keyword"
          label="社員番号・氏名・所属部署で絞り込む"
          placeholder="例: 山田、EMP-001、開発2課"
          :count="keyword ? totalCount : null"
          @search="applyKeyword"
        />

        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th rowspan="2">社員番号</th>
                <th rowspan="2">氏名</th>
                <th rowspan="2">所属部署</th>
                <th rowspan="2">役割</th>
                <th colspan="4" class="text-center" nowrap>有給(日)</th>
                <th rowspan="2" class="position-right" nowrap>代休(日)</th>
                <th rowspan="2"></th>
              </tr>
              <tr>
                <th><a href="#" class="paid-rule-link" @click.prevent="openPaidLeaveRuleModal">区分</a></th>
                <th class="position-right">所持日数</th>
                <th class="position-right">今期消化</th>
                <th class="position-right">付与月</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in items" :key="item.id || index">
                <td>{{ item.employee_id }}</td>
                <td><router-link :to="{ name: info, params: { id: item.id } }">{{ item.name }}</router-link></td>
                <td>{{ item.department_name || '' }}</td>
                <td class="role-cell">
                  <CBadge v-for="roleName in roleNames(item)" :key="roleName" color="info" class="mr-1 mb-1">{{ roleName }}</CBadge>
                  <span v-if="!roleNames(item).length" class="text-muted">未設定</span>
                </td>
                <td>{{ workingDaysLabel(item.working_days) }}</td>
                <td class="position-right">{{ numberLabel(item.paid_days) }}</td>
                <td class="position-right" :style="paidDaysUsedCellStyle(item.paid_days_used)">{{ numberLabel(item.paid_days_used) }}</td>
                <td class="position-right">{{ item.paid_days_add == null ? '' : item.paid_days_add + '月' }}</td>
                <td class="position-right">{{ numberLabel(item.comp_days) }}</td>
                <td><button @click="openModal(item)" class="btn btn-danger" :disabled="loading">無効</button></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-center">
          <VPagination
            v-model="currentPage"
            :click-handler="changePage"
            :page-count="totalPages"
            :force-page="currentPage"
            :force-ellipses="true"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-text="'<'"
            :next-text="'>'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :page-link-class="'page-link'"
            :class-prefix="'pagination'"
          />
        </div>
      </CCardBody>
    </CCard>

        <!--
          無効にした利用者。**開いたときだけ読む。**

          通常の一覧には混ぜない。あちらは全画面が使っていて、
          無効な人が混ざると承認者の候補や集計の対象に入りかねない。
        -->
        <div class="inactive-users">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm inactive-users__toggle"
            :disabled="inactiveLoading"
            @click="toggleInactive"
          >
            {{ showInactive ? '無効な利用者を閉じる' : '無効な利用者を表示' }}
          </button>

          <div v-if="showInactive" class="inactive-users__body">
            <p v-if="!inactiveItems.length && !inactiveLoading" class="text-muted mb-0">
              無効にした利用者はいません。
            </p>
            <table v-else class="table table-bordered mt-2 mb-0">
              <thead>
                <tr>
                  <th>社員番号</th>
                  <th>氏名</th>
                  <th>所属部署</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in inactiveItems" :key="row.id">
                  <td>{{ row.employee_id }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.department_name || '' }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      :disabled="inactiveLoading"
                      @click="restoreUser(row)"
                    >有効に戻す</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

    <!--
      v-show ではなく v-if。v-show は開いていなくても中身を描画するため、
      postItem が null のまま {{ val.employee_id }} を読んで
      TypeError: Cannot read properties of null になっていた。
      画面自体は出るが、開くたびにコンソールへ例外が積まれ、
      本当のエラーが埋もれる。削除モーダルは開くまで存在しなくてよい。
    -->
    <DeleteModal
      v-if="showModal"
      :draggable="true"
      :resizable="true"
      :val="postItem"
      :loading="loading"
      @delete="deleteUser"
      @close="closeModal"
    />

    <CModal :show.sync="showPaidLeaveRuleModal" @close="closePaidLeaveRuleModal" centered size="lg" class="paid-rule-modal">
      <CModalHeader :close-button="true">有休付与ルール</CModalHeader>
      <CModalBody class="paid-rule-modal-body">
        <div class="mb-1"><label class="mb-0">（1）通常の労働者の付与日数</label></div>
        <div class="table-responsive">
          <table class="table table-bordered mb-3">
            <tr>
              <th class="plr-rowhead">継続勤務年数（年）</th>
              <th v-for="(column, index) in paidLeaveNormalColumns" :key="'n-h' + index" class="plr-colhead text-center">{{ column.label }}</th>
            </tr>
            <tr>
              <th class="plr-rowhead">付与日数（日）</th>
              <td v-for="(column, index) in paidLeaveNormalColumns" :key="'n-d' + index" class="text-center">{{ column.days }}</td>
            </tr>
          </table>
        </div>

        <div class="mb-1"><label class="mb-0">（2）週所定労働日数が4日以下かつ週所定労働時間が30時間未満の労働者の付与日数</label></div>
        <div class="table-responsive">
          <table class="table table-bordered mb-1">
            <tr>
              <th rowspan="2" class="plr-rowhead text-center">週所定<br>労働日数</th>
              <th rowspan="2" class="plr-rowhead text-center">1年間の<br>所定労働日数※</th>
              <th colspan="7" class="plr-rowhead text-center">継続勤務年数（年）</th>
            </tr>
            <tr>
              <th v-for="(column, index) in paidLeaveShortColumns" :key="'s-h' + index" class="plr-colhead text-center">{{ column.label }}</th>
            </tr>
            <tr v-for="row in paidLeaveShortRows" :key="'s-r' + row.weeklyDays">
              <th class="plr-rowhead">{{ row.weeklyDays }}日</th>
              <td class="text-center">{{ row.annualRange }}</td>
              <td v-for="(column, index) in paidLeaveShortColumns" :key="'s-d' + row.weeklyDays + '-' + index" class="text-center">{{ row.days[column.key] }}</td>
            </tr>
          </table>
        </div>
        <div class="text-muted small">※週以外の期間によって労働日数が定められている場合</div>
      </CModalBody>
      <template #footer><button class="btn btn-secondary" @click="closePaidLeaveRuleModal">閉じる</button></template>
    </CModal>
  </div>
</template>

<script>
import ConstList from './../ConstList'
import DeleteModal from './DeleteModal'
import axios from 'axios'
import VPagination from 'vuejs-paginate'
import ListSearchBox from '@/common/ListSearchBox'

export default {
  name: 'List',
  components: { DeleteModal, VPagination, ListSearchBox },
  data () {
    return {
      showModal: false,
      postItem: null,
      items: [],
      errors: [],
      // 無効な利用者。**開くまで読まない。**
      showInactive: false,
      inactiveItems: [],
      inactiveLoading: false,
      registerTop: '',
      info: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      /* いま効いている絞り込みの言葉。空なら絞り込んでいない。 */
      keyword: '',
      totalCount: 0,
      loading: false,
      showPaidLeaveRuleModal: false,
      paidLeaveNormalColumns: [
        { label: '0.5', days: 10 }, { label: '1.5', days: 11 }, { label: '2.5', days: 12 },
        { label: '3.5', days: 14 }, { label: '4.5', days: 16 }, { label: '5.5', days: 18 }, { label: '6.5以上', days: 20 }
      ],
      paidLeaveShortColumns: [
        { label: '0.5', key: 'y05' }, { label: '1.5', key: 'y15' }, { label: '2.5', key: 'y25' },
        { label: '3.5', key: 'y35' }, { label: '4.5', key: 'y45' }, { label: '5.5', key: 'y55' }, { label: '6.5以上', key: 'y65' }
      ],
      paidLeaveShortRows: [
        { weeklyDays: 4, annualRange: '169～216日', days: { y05: 7, y15: 8, y25: 9, y35: 10, y45: 12, y55: 13, y65: 15 } },
        { weeklyDays: 3, annualRange: '121～168日', days: { y05: 5, y15: 6, y25: 6, y35: 8, y45: 9, y55: 10, y65: 11 } },
        { weeklyDays: 2, annualRange: '73～120日', days: { y05: 3, y15: 4, y25: 4, y35: 5, y45: 6, y55: 6, y65: 7 } },
        { weeklyDays: 1, annualRange: '48～72日', days: { y05: 1, y15: 2, y25: 2, y35: 2, y45: 3, y55: 3, y65: 3 } }
      ]
    }
  },
  created () {
    this.registerTop = ConstList.REGISTER_TOP
    this.info = ConstList.ROUTE_INFO
  },
  mounted () {
    this.callList()
    this.fetchTotalPages()
  },
  methods: {
    callList () {
      this.errors = []
      axios.get(process.env.VUE_APP_API_ORIGIN + '/users', {
        params: {
          currentPage: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.keyword || undefined
        }
      })
        .then(response => {
          this.items = Array.isArray(response.data) ? response.data : []
          if (!this.items.length) this.errors = [ConstList.EMPTY]
        })
        .catch(error => { this.errors = [this.resolveError(error, 'ユーザー一覧を取得できませんでした。')] })
    },
    roleNames (item) {
      return item && Array.isArray(item.role_names) ? item.role_names : []
    },
    numberLabel (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(1) : ''
    },
    workingDaysLabel (value) {
      const days = Number(value)
      if (!Number.isFinite(days)) return ''
      if (days === 5) return '通常'
      if (days === 0) return '付与無し'
      if (days >= 1 && days <= 4) return `週${days}勤務`
      return ''
    },
    paidDaysUsedCellStyle (value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return { color: 'black' }
      if (number < 3) return { color: 'red' }
      if (number < 5) return { color: 'orange' }
      return { color: 'black' }
    },
    openModal (item) {
      if (this.loading) return
      this.postItem = item
      this.showModal = true
    },
    deleteUser () {
      if (!this.postItem) return
      this.loading = true
      const params = new URLSearchParams()
      params.append('id', this.postItem.id)
      axios.post(process.env.VUE_APP_API_ORIGIN + '/delete', params)
        .then(response => {
          /*
           * **固定の文言に差し替えない。**断る理由は複数ある
           * （承認待ちが残っている／最後の最高管理者／権限が無い）。
           * 決め打ちすると、実際と違うことを言う。言われたとおりに
           * 動いても直らない。
           */
          if (response.data !== ConstList.SUCCESS) throw new Error('ユーザーを無効にできませんでした。')
          // **共通の「削除が完了しました」を使わない。**行は消えていない。
          alert('無効にしました。「無効な利用者を表示」から有効に戻せます。')
          this.showModal = false
          return Promise.all([this.callList(), this.fetchTotalPages()])
        })
        .catch(error => { this.errors = [this.resolveError(error, 'ユーザーを無効にできませんでした。')] })
        .finally(() => { this.loading = false })
    },
    closeModal () { this.showModal = false },

    /** 無効な利用者の節を開閉する。**開いたときだけ読む。** */
    toggleInactive () {
      this.showInactive = !this.showInactive
      if (this.showInactive) this.loadInactive()
    },

    loadInactive () {
      this.inactiveLoading = true
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/users/inactive')
        .then(response => { this.inactiveItems = response.data || [] })
        .catch(error => { this.errors = [this.resolveError(error, '無効な利用者を取得できませんでした。')] })
        .finally(() => { this.inactiveLoading = false })
    },

    /**
     * 有効に戻す。
     *
     * **席数が足りなければサーバが断る。**契約の上限はDBの引き金が
     * 見ているので、画面では数えない。数えると必ずどちらかが古くなる。
     */
    restoreUser (row) {
      if (!row || !row.id) return
      if (!window.confirm(row.name + ' さんを有効に戻しますか？')) return
      this.inactiveLoading = true
      axios.post(process.env.VUE_APP_API_ORIGIN + '/users/' + row.id + '/restore')
        .then(response => {
          if (response.data !== ConstList.SUCCESS) throw new Error('有効に戻せませんでした。契約の利用者上限に達している可能性があります。')
          return Promise.all([this.loadInactive(), this.callList(), this.fetchTotalPages()])
        })
        .catch(error => { this.errors = [this.resolveError(error, '有効に戻せませんでした。契約の利用者上限に達している可能性があります。')] })
        .finally(() => { this.inactiveLoading = false })
    },
    changePage (page) { this.currentPage = page; this.callList() },

    /**
     * 絞り込みを受ける。
     *
     * **ページを1へ戻す。**5ページ目のまま絞り込むと、結果が1ページ
     * しか無いときに空の表になる。件数も先に数え直す。
     */
    applyKeyword (keyword) {
      this.keyword = keyword
      this.currentPage = 1
      this.fetchTotalPages().then(() => this.callList())
    },
    fetchTotalPages () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/users/count', {
        params: { keyword: this.keyword || undefined }
      })
        .then(response => {
          const totalCount = Number(response.data)
          this.totalCount = Number.isFinite(totalCount) ? totalCount : 0
          this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        })
        .catch(error => { this.errors = [this.resolveError(error, 'ユーザー件数を取得できませんでした。')] })
    },
    openPaidLeaveRuleModal () { this.showPaidLeaveRuleModal = true },
    closePaidLeaveRuleModal () { this.showPaidLeaveRuleModal = false },
    resolveError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data && data !== 'NG') return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
table { width: 100%; }
.table td.position-right, .table th.position-right { text-align: right !important; padding-right: 10px; }
td { vertical-align: middle; white-space: nowrap; padding-top: 4px; padding-bottom: 4px; }
.role-cell { white-space: normal; min-width: 180px; }
@media (max-width: 576px) {
  ::v-deep .paid-rule-modal .modal-dialog { max-width: 100vw; width: 100vw; margin: 0; }
  ::v-deep .paid-rule-modal .modal-content { border-radius: 0; }
}
</style>
